import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, AlertCircle, RefreshCw, X, Filter, Box, ArrowUpDown } from 'lucide-react';
import api from './axiosConfig';
import toast from 'react-hot-toast';
import { usePermissions } from './usePermissions';
import { useTranslation } from 'react-i18next'; // 🎯 الترجمة
import { exportToExcel } from './exportUtils';
import { useFontSize } from './FontSizeContext';
import StatusBadge from './StatusBadge';

const IncidentList = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language.startsWith('ar') || i18n.language.startsWith('ur');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canCreate } = usePermissions('incidents'); // 🛡️ الصلاحيات
  
  // --- Session Data ---
  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const sessionSite = session.site_id || '';
  const sessionOrg = session.org_id || '';

  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;

  // --- State ---
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [showAllSites, setShowAllSites] = useState(searchParams.get('all_sites') === 'true');

  // --- URL Params (Persistence) ---
  const page = parseInt(searchParams.get('page')) || 1;
  const limit = 50;
  const q = searchParams.get('q') || "";
  const status = searchParams.get('status') || "";
  const reported_by = searchParams.get('reported_by') || "";
  const sortBy = searchParams.get('sortBy') || "report_date";
  const sortDir = searchParams.get('sortDir') || "desc";

  const fetchIncidents = useCallback(async () => {
    setLoading(true);
    try {
      const skip = (page - 1) * limit;
      const effectiveSite = !showAllSites && !searchParams.get('site_id') ? sessionSite : searchParams.get('site_id');

      const res = await api.get('/incidents', {
        params: {
          org_id: sessionOrg,
          site_id: effectiveSite,
          all_sites: showAllSites,
          search: q,
          status: status,
          reported_by: reported_by,
          skip: skip,
          limit: limit,
          sortBy: sortBy,
          sortDir: sortDir
        }
      });
      
      setIncidents(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error(t('common.load_error', "Failed to load incidents"));
    } finally {
      setLoading(false);
    }
  }, [page, limit, q, status, reported_by, sortBy, sortDir, showAllSites, searchParams, sessionSite, sessionOrg, t]);

  useEffect(() => {
    const timer = setTimeout(() => fetchIncidents(), 100);
    return () => clearTimeout(timer);
  }, [fetchIncidents]);

  // --- Filter Handlers ---
  const updateFilters = (newFilters) => {
    const params = Object.fromEntries(searchParams.entries());
    const updated = { ...params, ...newFilters };
    Object.keys(updated).forEach(key => !updated[key] && delete updated[key]);
    setSearchParams(updated);
  };

  const handleSort = (column) => {
    const direction = sortBy === column && sortDir === 'asc' ? 'desc' : 'asc';
    updateFilters({ sortBy: column, sortDir: direction, page: 1 });
  };

  const handleClearFilters = () => {
    setSearchParams({});
    setShowAllSites(false);
  };

  const handleRowClick = (id) => {
    const activeFilters = Object.fromEntries(searchParams.entries());
    const effectiveSite = !showAllSites && !activeFilters.site_id ? sessionSite : activeFilters.site_id;
    let navParams = { ...activeFilters, org_id: sessionOrg };
    if (effectiveSite) navParams.site_id = effectiveSite; else delete navParams.site_id;
    if (showAllSites) navParams.all_sites = true;
    
    const queryParams = new URLSearchParams(navParams).toString();
    navigate(`/incidents/${id}${queryParams ? '?' + queryParams : ''}`);
  };

  // --- Sidebar Events ---
  useEffect(() => {
    const openAllSites = () => { setShowAllSites(true); updateFilters({ page: 1, all_sites: true }); };
    const viewMySite = () => { setShowAllSites(false); updateFilters({ page: 1, all_sites: false }); };
    
    const exportData = () => {
        const activeFilters = Object.fromEntries(searchParams.entries());
        const effectiveSite = !showAllSites && !activeFilters.site_id ? sessionSite : activeFilters.site_id;
        let exportParams = { ...activeFilters, org_id: sessionOrg, search: q };
        if (effectiveSite) exportParams.site_id = effectiveSite; else delete exportParams.site_id;

        const columnsMap = {
            "ticket_id": t('incidents.fields.ticket_id', 'Ticket ID'),
            "description": t('common.description', 'Summary'),
            "status": t('common.status', 'Status'),
            "priority": t('incidents.fields.priority', 'Priority'),
            "reported_by": t('incidents.fields.reported_by', 'Reported By'),
            "report_date": t('common.date', 'Date')
        };
        exportToExcel('incidents', exportParams, columnsMap, t);
    };

    window.addEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
    window.addEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
    window.addEventListener('GLOBAL_EXPORT', exportData);
    
    return () => {
      window.removeEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
      window.removeEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
      window.removeEventListener('GLOBAL_EXPORT', exportData);
    };
  }, [searchParams, showAllSites, sessionSite, sessionOrg, q, t]);

  const totalPages = Math.ceil(total / limit) || 1;

  return (
    <div className={`flex flex-col h-full bg-surface-app  overflow-hidden uppercase tracking-tighter ${isRTL ? 'font-cairo text-right' : 'font-sans text-left'}`} style={{ fontSize: `${fontSize}px` }} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="bg-surface-card p-3 border-b flex justify-between items-center shadow-sm z-20">
        <div className={`flex items-center gap-4 ${isRTL ? 'mr-4' : 'ml-4'}`}>
          <h1 className="text-primary font-black italic text-lg flex items-center gap-2">
            <AlertCircle size={24} className="text-status-warning" /> {t('incidents.list.title', 'Incidents Registry')}
            <span className={`text-[0.9em] px-2 py-0.5 rounded-full border not-italic font-bold ml-2 ${showAllSites ? 'bg-status-danger-bg text-status-danger border-status-danger-bg' : 'bg-status-info-bg text-status-info border-status-info-bg'}`}>
                {showAllSites ? t('common.global_view', 'GLOBAL VIEW') : `${t('common.site', 'SITE')}: ${sessionSite}`}
            </span>
          </h1>
        </div>

        <div className={`flex gap-3 items-center ${isRTL ? 'ml-4' : 'mr-4'}`}>
          <button onClick={handleClearFilters} className="flex items-center gap-2 px-4 py-2 text-ink-secondary hover:text-status-danger font-bold transition-colors">
            <X size={16}/> {t('common.clear_filters', 'CLEAR FILTERS')}
          </button>
          <button onClick={() => fetchIncidents()} className="p-2 text-ink-secondary hover:text-primary transition-colors" title="Refresh">
            <RefreshCw size={16} className={loading ? "animate-spin text-status-warning" : ""} />
          </button>
          {canCreate && (
            <button onClick={() => navigate('/incidents/new')} className="bg-primary text-white px-6 py-2 rounded font-black hover:bg-primary-dark shadow-md transition-all active:scale-95 flex items-center gap-2">
              <Plus size={14}/> {t('incidents.list.new', 'NEW INCIDENT')}
            </button>
          )}
        </div>
      </div>

      {/* Table Area */}
      <div className="flex-1 overflow-auto p-4 custom-scrollbar">
        <div className="bg-surface-card border shadow-sm rounded-sm overflow-hidden min-h-[450px]">
          <table className="w-full text-left border-collapse">
            <thead className="bg-surface-panel border-b text-ink font-black uppercase text-[0.9em] sticky top-0 z-10 tracking-widest">
              <tr>
                <SortableHeader label={t('incidents.fields.ticket_id', 'Ticket ID')} sortKey="ticket_id" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} isRTL={isRTL} />
                <SortableHeader label={t('assets.headers.desc', 'Summary')} sortKey="description" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} isRTL={isRTL} />
                <SortableHeader label={t('assets.headers.status', 'Status')} sortKey="status" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="center" isRTL={isRTL} />
                <SortableHeader label={t('incidents.fields.priority', 'Priority')} sortKey="priority" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="center" isRTL={isRTL} />
                <SortableHeader label={t('incidents.fields.reported_by', 'Reported By')} sortKey="reported_by" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} isRTL={isRTL} />
                <SortableHeader label={t('sr.fields.reportdate', 'Date')} sortKey="report_date" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="center" isRTL={isRTL} />
                <th className="p-3 border-x border-line text-center w-24">{t('common.site', 'Site')}</th>
              </tr>
              {/* Filter Row */}
              <tr className="bg-surface-card border-b border-line">
                <td className="p-1 border-x border-line-subtle">
                  <div className={`relative flex items-center `}>
                    <Search className={`absolute text-ink-secondary ${isRTL ? 'right-2' : 'left-2'}`} size={12} />
                    <FilterInput value={q} onChange={v => updateFilters({ q: v, page: 1 })} placeholder={t('common.search')} className={isRTL ? 'pr-6' : 'pl-6'}/>
                  </div>
                </td>
                <td className="p-1 border-x border-line-subtle bg-surface-panel/30"></td>
                <td className="p-1 border-x border-line-subtle">
                  <select className={`w-full p-1.5 text-primary font-bold outline-none bg-transparent cursor-pointer ${isRTL ? 'text-right' : 'text-center'}`} value={status} onChange={e => updateFilters({ status: e.target.value, page: 1 })}>
                    <option value="">{t('common.all', 'ALL')}</option>
                    <option value="NEW">NEW</option>
                    <option value="INPRG">INPRG</option>
                    <option value="RESOLVED">RESOLVED</option>
                    <option value="CLOSED">CLOSED</option>
                  </select>
                </td>
                <td className="p-1 border-x border-line-subtle bg-surface-panel/30"></td>
                <td className="p-1 border-x border-line-subtle"><FilterInput value={reported_by} onChange={v => updateFilters({ reported_by: v, page: 1 })} /></td>
                <td colSpan="2" className="p-1 border-x border-line-subtle bg-surface-panel/30"></td>
              </tr>
            </thead>

            <tbody className="divide-y divide-line-subtle font-bold">
              {loading ? (
                <tr><td colSpan="7" className="p-20 text-center animate-pulse font-black text-ink-secondary uppercase tracking-[0.5em]">{t('common.syncing', 'Syncing Data...')}</td></tr>
              ) : incidents.length === 0 ? (
                <tr><td colSpan="7" className="p-20 text-center text-ink-secondary italic font-black uppercase tracking-widest">{t('common.no_records', 'No records found')}</td></tr>
              ) : incidents.map((inc) => (
                <tr
                  key={inc.id}
                  onClick={() => handleRowClick(inc.id)}
                  className={`hover:bg-primary-soft cursor-pointer transition-all group ${isRTL ? 'text-right' : 'text-left'}`}
                >
                  <td className="p-3 border-x border-line-subtle font-black text-primary group-hover:underline">{inc.ticket_id}</td>
                  <td className="p-3 border-x border-line-subtle text-ink truncate max-w-xs">{inc.description}</td>
                  <td className="p-3 border-x border-line-subtle text-center">
                    <StatusBadge status={inc.status} />
                  </td>
                  <td className="p-3 border-x border-line-subtle text-center">
                      <span className={`font-black ${inc.priority === 1 ? 'text-status-danger' : 'text-ink-secondary'}`}>{inc.priority}</span>
                  </td>
                  <td className="p-3 border-x border-line-subtle italic text-ink-secondary">{inc.reported_by || '---'}</td>
                  <td className="p-3 border-x border-line-subtle text-center text-ink-secondary font-mono">{new Date(inc.report_date).toLocaleDateString()}</td>
                  <td className="p-3 border-x border-line-subtle text-center text-ink">{inc.site_id || '---'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Pagination Footer */}
      <div className={`bg-surface-card border-t border-line p-2 px-8 flex justify-between items-center shadow-sm z-30 font-black text-[10px] text-ink-secondary `}>
        <div className={`flex items-center gap-6 `}>
            <div className={`flex items-center gap-2 `}>
                <Box size={14} className="text-primary"/>
                <span>{t('common.showing', 'Showing')}: <span className="text-primary">{(page-1)*limit+1}-{Math.min(page*limit, total)}</span></span>
            </div>
            <div className={`flex items-center gap-2 border-line ${isRTL ? 'border-r pr-6 flex-row-reverse' : 'border-l pl-6'}`}>
                <Filter size={14} className="text-status-warning"/>
                <span>{t('common.total', 'Total')}: <span className="text-status-warning">{total}</span></span>
            </div>
        </div>

        <div className={`flex items-center gap-4 `}>
            <div className={`flex items-center gap-1 bg-surface-panel rounded px-2 py-1 border border-line `}>
                <button disabled={page === 1} onClick={() => updateFilters({page: 1})} className="p-1.5 hover:text-primary disabled:opacity-30 transition-all"><ChevronsLeft size={16} className={isRTL ? 'rotate-180' : ''}/></button>
                <button disabled={page === 1} onClick={() => updateFilters({page: page - 1})} className="p-1.5 hover:text-primary disabled:opacity-30 transition-all"><ChevronLeft size={16} className={isRTL ? 'rotate-180' : ''}/></button>
                <div className="px-4 text-[11px] font-mono border-x border-line mx-1">
                    <span className="text-primary text-sm">{page}</span> / {totalPages}
                </div>
                <button disabled={page >= totalPages} onClick={() => updateFilters({page: page + 1})} className="p-1.5 hover:text-primary disabled:opacity-30 transition-all"><ChevronRight size={16} className={isRTL ? 'rotate-180' : ''}/></button>
                <button disabled={page >= totalPages} onClick={() => updateFilters({page: totalPages})} className="p-1.5 hover:text-primary disabled:opacity-30 transition-all"><ChevronsRight size={16} className={isRTL ? 'rotate-180' : ''}/></button>
            </div>
        </div>
      </div>
    </div>
  );
};

// --- Helper Components ---
const SortableHeader = ({ label, sortKey, sortBy, sortDir, onSort, align = "left", isRTL }) => (
  <th className={`p-3 border-x border-line cursor-pointer hover:bg-primary-soft transition-colors text-${align === 'center' ? 'center' : (isRTL ? 'right' : 'left')}`} onClick={() => onSort(sortKey)}>
      <div className={`flex items-center gap-2 ${align === 'center' ? 'justify-center' : ''} `}>
          {label}
          <ArrowUpDown size={12} className={sortBy === sortKey ? 'text-primary' : 'text-ink-secondary'} />
      </div>
  </th>
);

const FilterInput = ({ value, onChange, placeholder, center = false, className = "" }) => (
  <input
    className={`w-full p-1.5 text-primary font-bold outline-none bg-transparent placeholder-ink-secondary ${center ? 'text-center' : ''} ${className}`}
    placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)}
  />
);

export default IncidentList;